import { Check, ChevronDown, Search } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "./cn";

/**
 * The control surface, shared by every input, select and textarea.
 *
 * There used to be four of these — `inputClass`, the queue filter bar's
 * `fieldClass`, the reports/chase date input, and the review drawer's textarea —
 * and only one of the four had a focus ring, so tabbing across a filter bar
 * silently lost the caret. Everything routes through this now.
 *
 * The outline itself comes from the global `:focus-visible` rule in styles.css.
 * Text controls match `:focus-visible` however they were focused, including by
 * mouse, so it applies here exactly as it should.
 */
export const controlClass =
  "w-full rounded-md border border-line bg-surface text-text text-sm " +
  "placeholder:text-subtle transition-colors " +
  "focus:border-accent " +
  "disabled:bg-surface-sunken disabled:text-subtle disabled:cursor-not-allowed";

/** Back-compat alias — the old name, used by the twelve dialogs. */
export const inputClass = cn(controlClass, "h-9 px-3");

const INVALID = "border-danger-solid focus:border-danger-solid";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className = "", ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(controlClass, "h-9 px-3", invalid && INVALID, className)}
      {...rest}
    />
  );
});

/** Long enough that typing does not fire per keystroke, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 300;

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> {
  /** The applied term — usually URL state, so it can change from outside. */
  value: string;
  onChange: (next: string) => void;
  debounceMs?: number;
}

/**
 * A search box that keeps its own keystrokes and reports the settled term.
 *
 * The debounce belongs in here rather than at each call site: pushing a URL
 * update per keystroke costs a history entry per letter, so the back button
 * walks the user through their own typing. The two pieces of state are the
 * point — `draft` is what the caret sees, `value` is what has been applied.
 */
export function SearchInput({
  value,
  onChange,
  debounceMs = SEARCH_DEBOUNCE_MS,
  className = "",
  ...rest
}: SearchInputProps) {
  const [draft, setDraft] = useState(value);

  // Resync during render rather than in an effect when `value` changes from
  // elsewhere — a cleared filter, a switched tab, the back button. An effect
  // would render the stale draft once first, so the box visibly flickers back
  // to what the user just cleared.
  const [lastApplied, setLastApplied] = useState(value);
  if (value !== lastApplied) {
    setLastApplied(value);
    setDraft(value);
  }

  // `onChange` is a fresh closure each render, so it is read from a ref rather
  // than depended on: depending on it would clear the timer every keystroke and
  // the debounce would never fire.
  const changeRef = useRef(onChange);
  useEffect(() => {
    changeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => changeRef.current(draft), debounceMs);
    return () => clearTimeout(timer);
  }, [draft, value, debounceMs]);

  // `h-fit`: in a grid or flex row the wrapper would otherwise stretch to the
  // row's height, and the icon — centred on the wrapper — drops below the box.
  return (
    <div className="relative h-fit">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        maxLength={120}
        className={cn("pl-9", className)}
        {...rest}
      />
    </div>
  );
}

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ invalid, className = "", rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={invalid || undefined}
        className={cn(controlClass, "px-3 py-2 resize-y", invalid && INVALID, className)}
        {...rest}
      />
    );
  }
);

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/**
 * A native `<select>` with our chrome on it.
 *
 * Native on purpose. A custom listbox would have to re-implement type-ahead,
 * mobile's native picker sheet, and form semantics — and two e2e specs address
 * these by `locator("select")`. `appearance-none` plus our own chevron gets the
 * look without giving up any of that. The chevron is `pointer-events-none` so
 * clicks fall through to the control underneath.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className = "", children, ...rest },
  ref
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          controlClass,
          "h-9 pl-3 pr-8 appearance-none cursor-pointer",
          invalid && INVALID,
          className
        )}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
      />
    </div>
  );
});

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
}

/**
 * A styled native checkbox.
 *
 * `appearance-none` on the real input, with the tick drawn over it — so it keeps
 * `role="checkbox"`, the indeterminate property, label association and form
 * participation, none of which a div can fake.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, className = "", id, ...rest }, ref) {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const control = (
      <span className="relative inline-grid shrink-0 place-items-center">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className={cn(
            "peer h-4 w-4 cursor-pointer appearance-none rounded-sm border border-line-strong bg-surface",
            "transition-colors checked:border-accent checked:bg-accent",
            "indeterminate:border-accent indeterminate:bg-accent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...rest}
        />
        <Check
          aria-hidden="true"
          className="pointer-events-none absolute h-3 w-3 text-accent-contrast opacity-0 peer-checked:opacity-100"
          strokeWidth={3.5}
        />
      </span>
    );

    if (!label) return control;

    return (
      <label
        htmlFor={inputId}
        className="inline-flex cursor-pointer items-center gap-2 text-sm text-text"
      >
        {control}
        {label}
      </label>
    );
  }
);

/**
 * Label + control + optional hint and error.
 *
 * Wraps in a real `<label>` when it owns the control, so clicking the label text
 * focuses the input for free.
 *
 * `group` is for the case where it owns *several* — a set of checkboxes has no
 * single control for a label to name. Without it the wrapper `<label>` bound
 * itself to the first labelable descendant it could find, so clicking the
 * heading "CONTRACTORS (OPTIONAL)" ticked the first contractor in the list and
 * assigned them to the project. It also nested a `<label>` per row inside the
 * outer one, which is not valid HTML.
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className = "",
  group = false,
}: {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  /** The children are a set of controls, each already labelled, not one field. */
  group?: boolean;
}) {
  const labelId = useId();
  const body = (
    <>
      <span
        id={group ? labelId : undefined}
        className="flex items-center gap-1 text-micro font-medium uppercase tracking-wider text-subtle"
      >
        {label}
        {required && (
          <span className="text-danger-on-soft" aria-hidden="true">
            *
          </span>
        )}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && !error && <p className="mt-1 text-xs text-subtle">{hint}</p>}
      {error && (
        <p className="mt-1 text-xs text-danger-on-soft" role="alert">
          {error}
        </p>
      )}
    </>
  );

  if (group) {
    return (
      <div role="group" aria-labelledby={labelId} className={cn("block", className)}>
        {body}
      </div>
    );
  }

  return <label className={cn("block", className)}>{body}</label>;
}

/** Per-field messages from the API's `fieldErrors` envelope. */
export function FieldError({ messages }: { messages?: string[] }) {
  if (!messages?.length) return null;
  return (
    <p className="mt-1 text-xs text-danger-on-soft" role="alert">
      {messages.join(" ")}
    </p>
  );
}
